import useDeviceDetails from "../hooks/useDeviceDetails";
import { useFlags } from "launchdarkly-react-client-sdk";
import { isRmmAlertsLdEnabled } from "../utils/rmmAlertsAccess";
import DeviceAlertHistory from "../components/DeviceAlertHistory";
import DeviceDataNotices, { sectionCount, sectionHasData, displayDeviceMetrics } from "../components/DeviceDataNotices";
/**
 * Device detail: Theo chat + snapshot context for one source-owned device.
 * Route: /devices/:sourceType/:sourceId/:deviceId(/:threadId)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddCommentIcon from "@mui/icons-material/AddComment";
import AddIcon from "@mui/icons-material/Add";
import AppsIcon from "@mui/icons-material/Apps";
import ComputerIcon from "@mui/icons-material/Computer";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MemoryIcon from "@mui/icons-material/Memory";
import RefreshIcon from "@mui/icons-material/Refresh";
import SecurityIcon from "@mui/icons-material/Security";
import StorageIcon from "@mui/icons-material/Storage";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from "react-resizable-panels";
import NeoAssistant from "../components/NeoAssistant";
import DeviceOsIcon, {
  deviceOsFamilyLabel,
  resolveDeviceOsFamilyFromHints,
} from "../components/DeviceOsIcon";
import DevicesApi, {
  DeviceIdentityParams,
  DeviceSnapshotDetailsResponse as RmmDeviceSnapshotDetailsResponse,
  DeviceSnapshotDiskMetric as RmmDeviceSnapshotDiskMetric,
  DeviceSnapshotSample as RmmDeviceSnapshotSample,
  DeviceThreadSummary as RmmDeviceThreadSummary,
} from "../apis/devicesApi";
import { ChatAction } from "../actions/chatAction";
import { ActionTypes } from "../store/actionTypes";
import { useAppDispatch, useAppSelector } from "../hooks/storeHook";
import { DeleteModal } from "../shared/components";

const panelSx = {
  border: "1px solid #E2E8F0",
  borderRadius: "6px",
  bgcolor: "#FFFFFF",
};

const APP_LATEST_FILTER_ALL = "all";
const APP_LATEST_FILTER_CURRENT = "current";
const APP_LATEST_FILTER_NEWER = "newer";
const APP_LATEST_FILTER_SYSTEM = "system";
const APP_LATEST_FILTER_NOT_IN_CATALOG = "not_in_catalog";
const APP_LATEST_FILTER_PENDING = "pending";
const APP_LATEST_FILTER_UNKNOWN = "unknown";

/** Keep in sync with softwareVersionStatus().kind. */
type AppLatestKind =
  | typeof APP_LATEST_FILTER_CURRENT
  | typeof APP_LATEST_FILTER_NEWER
  | typeof APP_LATEST_FILTER_SYSTEM
  | typeof APP_LATEST_FILTER_NOT_IN_CATALOG
  | typeof APP_LATEST_FILTER_PENDING
  | typeof APP_LATEST_FILTER_UNKNOWN;
type AppLatestFilter = typeof APP_LATEST_FILTER_ALL | AppLatestKind;

const APP_LATEST_FILTER_LABELS: Record<AppLatestFilter, string> = {
  [APP_LATEST_FILTER_ALL]: "All latest",
  [APP_LATEST_FILTER_CURRENT]: "Current",
  [APP_LATEST_FILTER_NEWER]: "Newer",
  [APP_LATEST_FILTER_SYSTEM]: "System app",
  [APP_LATEST_FILTER_NOT_IN_CATALOG]: "Not in catalog",
  [APP_LATEST_FILTER_PENDING]: "Lookup pending",
  [APP_LATEST_FILTER_UNKNOWN]: "Latest unavailable",
};

const APP_LATEST_FILTER_ORDER: AppLatestKind[] = [
  APP_LATEST_FILTER_CURRENT,
  APP_LATEST_FILTER_NEWER,
  APP_LATEST_FILTER_SYSTEM,
  APP_LATEST_FILTER_NOT_IN_CATALOG,
  APP_LATEST_FILTER_PENDING,
  APP_LATEST_FILTER_UNKNOWN,
];

interface ApplicationFilterState {
  name: string;
  latest: AppLatestFilter;
}

const compactFilterSx = {
  "& .MuiInputBase-root": { fontSize: "0.8rem" },
  "& .MuiInputLabel-root": { fontSize: "0.8rem" },
};

function rawRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

/** Always returns an object so callers can read nested fields without null checks. */
function asRecord(value: unknown): Record<string, unknown> {
  return rawRecord(value) || {};
}

function rawArray(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? value.filter((item): item is Record<string, unknown> => Boolean(rawRecord(item))) : [];
}

function rawString(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

function rawField(source: unknown, ...path: string[]): unknown {
  let cursor: unknown = source;
  for (const key of path) {
    const record = rawRecord(cursor);
    if (!record || !(key in record)) return undefined;
    cursor = record[key];
  }
  return cursor;
}

function providerDisplayName(provider: string): string {
  const value = String(provider || "").trim().toLowerCase();
  if (value === "datto") return "Datto";
  if (value === "ninjaone") return "NinjaOne";
  if (value === "intune") return "Intune";
  return value || "device source";
}

function requireDeviceIdentity(
  routeIdentity: DeviceIdentityParams | null,
  details?: RmmDeviceSnapshotDetailsResponse | null
): DeviceIdentityParams {
  if (!routeIdentity?.sourceType || !routeIdentity?.sourceId || !routeIdentity?.deviceId) {
    throw new Error("Device source identity is incomplete");
  }
  if (details) {
    const detailDeviceId = rawString(details.device_id);
    if (detailDeviceId && detailDeviceId !== routeIdentity.deviceId) {
      throw new Error("Device snapshot id does not match the route device id");
    }
    const source = details.source || {};
    const detailSourceType = rawString(source.source_type);
    const detailSourceId = rawString(source.source_id);
    if (detailSourceType && detailSourceType !== routeIdentity.sourceType) {
      throw new Error("Device snapshot source type does not match the route");
    }
    if (detailSourceId && detailSourceId !== String(routeIdentity.sourceId)) {
      throw new Error("Device snapshot source id does not match the route");
    }
  }
  return routeIdentity;
}

function formatBytes(value: unknown): string {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: size >= 10 ? 1 : 2 }).format(size)} ${units[unitIndex]}`;
}

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function percentOrNull(value: unknown): number | null {
  const parsed = numberOrNull(value);
  if (parsed === null) return null;
  if (parsed >= 0 && parsed <= 1) return Math.round(parsed * 1000) / 10;
  if (parsed < 0 || parsed > 100) return null;
  return Math.round(parsed * 10) / 10;
}

function formatUnknownValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return new Intl.NumberFormat().format(value);
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

function formatTime(ms: unknown): string {
  const value = Number(ms);
  if (!Number.isFinite(value) || value <= 0) return "—";
  return new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function diskLabel(disk: RmmDeviceSnapshotDiskMetric): string {
  return rawString(disk.disk_identifier) || rawString(disk.description) || "Disk";
}

function diskUsageParts(disk: RmmDeviceSnapshotDiskMetric): { used_bytes: number; free_bytes: number; total_bytes: number } | null {
  const explicitTotal = numberOrNull(disk.size_bytes);
  const explicitFree = numberOrNull(disk.free_bytes);
  const explicitUsed = numberOrNull(disk.used_bytes);
  const usedPercent = percentOrNull(disk.used_percent);

  let totalBytes = explicitTotal;
  let freeBytes = explicitFree;
  let usedBytes = explicitUsed;

  if (usedBytes === null && totalBytes !== null && freeBytes !== null) usedBytes = Math.max(0, totalBytes - freeBytes);
  if (freeBytes === null && totalBytes !== null && usedBytes !== null) freeBytes = Math.max(0, totalBytes - usedBytes);
  if (totalBytes === null && usedBytes !== null && freeBytes !== null) totalBytes = usedBytes + freeBytes;

  if (usedPercent !== null && totalBytes !== null && (usedBytes === null || freeBytes === null)) {
    usedBytes = (totalBytes * usedPercent) / 100;
    freeBytes = Math.max(0, totalBytes - usedBytes);
  }
  if (usedPercent !== null && freeBytes !== null && totalBytes === null && usedPercent < 100) {
    totalBytes = freeBytes / (1 - usedPercent / 100);
    usedBytes = Math.max(0, totalBytes - freeBytes);
  }
  if (usedPercent !== null && usedBytes !== null && totalBytes === null && usedPercent > 0) {
    totalBytes = usedBytes / (usedPercent / 100);
    freeBytes = Math.max(0, totalBytes - usedBytes);
  }

  if (usedBytes === null || freeBytes === null || totalBytes === null || totalBytes <= 0) return null;
  return {
    used_bytes: Math.max(0, usedBytes),
    free_bytes: Math.max(0, freeBytes),
    total_bytes: Math.max(0, totalBytes),
  };
}

function aggregateDiskUsage(metrics: RmmDeviceSnapshotSample["metrics"]): { used_percent: number; used_bytes: number; total_bytes: number; disk_count: number } | null {
  const metricDisks = metrics?.disks;
  const disks: RmmDeviceSnapshotDiskMetric[] = Array.isArray(metricDisks) ? metricDisks : [];
  let usedBytes = 0;
  let freeBytes = 0;
  let diskCount = 0;
  for (const disk of disks) {
    const parts = diskUsageParts(disk);
    if (!parts) continue;
    usedBytes += parts.used_bytes;
    freeBytes += parts.free_bytes;
    diskCount += 1;
  }
  const totalBytes = usedBytes + freeBytes;
  if (!diskCount || totalBytes <= 0) return null;
  return {
    used_percent: Math.round((usedBytes / totalBytes) * 1000) / 10,
    used_bytes: usedBytes,
    total_bytes: totalBytes,
    disk_count: diskCount,
  };
}

function getSoftwareRows(sample: RmmDeviceSnapshotSample | null): Array<Record<string, unknown>> {
  if (!sectionHasData(sample, "software")) return [];
  const directRows = rawArray(sample?.raw?.software);
  if (directRows.length) return directRows;
  const detected = rawRecord(sample?.raw?.detected_apps);
  const detectedApps = rawArray(detected?.apps);
  if (detectedApps.length) return detectedApps;
  const software = rawRecord(sample?.raw?.software);
  return rawArray(software?.software || software?.data || software?.applications || software?.installedSoftware || software?.items || software?.results || software?.rows);
}

function getOpenAlertRows(sample: RmmDeviceSnapshotSample | null): Array<Record<string, unknown>> {
  const openAlerts = rawRecord(sample?.raw?.open_alerts);
  return rawArray(openAlerts?.alerts);
}

function alertDescription(alert: Record<string, unknown>): string {
  const context = rawRecord(alert.alertContext);
  return (
    rawString(alert.message) ||
    rawString(alert.subject) ||
    rawString(context?.description) ||
    rawString(alert.diagnostics) ||
    rawString(alert.sourceName) ||
    rawString(context?.type) ||
    rawString(alert.sourceType) ||
    rawString(alert.uid) ||
    rawString(alert.alertUid) ||
    "RMM alert"
  ).replace(/\s+/g, " ");
}

function alertTimestamp(alert: Record<string, unknown>): number {
  const context = rawRecord(alert.alertContext);
  const raw =
    alert.timestamp ||
    alert.updateTime ||
    alert.updatedAt ||
    alert.createTime ||
    alert.createdAt ||
    context?.lastTriggered;
  const numeric = Number(raw);
  if (Number.isFinite(numeric)) return numeric > 10_000_000_000 ? numeric : numeric * 1000;
  const parsed = Date.parse(String(raw || ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function dedupeAlerts(alerts: Array<Record<string, unknown>>): Array<Record<string, unknown> & { duplicate_count: number }> {
  const byText = new Map<string, Record<string, unknown> & { duplicate_count: number }>();
  for (const alert of alerts) {
    const text = alertDescription(alert);
    const key = text.toLowerCase();
    const existing = byText.get(key);
    if (!existing) {
      byText.set(key, { ...alert, duplicate_count: 1 });
      continue;
    }
    const duplicateCount = existing.duplicate_count + 1;
    byText.set(key, alertTimestamp(alert) > alertTimestamp(existing) ? { ...alert, duplicate_count: duplicateCount } : { ...existing, duplicate_count: duplicateCount });
  }
  return Array.from(byText.values()).sort((a, b) => alertTimestamp(b) - alertTimestamp(a));
}

function softwareName(row: Record<string, unknown>): string {
  return rawString(row.name || row.displayName || row.applicationName || row.productName);
}

function softwareVersion(row: Record<string, unknown>): string {
  return rawString(row.version || row.currentVersion || row.installedVersion);
}

function softwareLatestVersion(row: Record<string, unknown>): string {
  return rawString(
    row.latestVersion ||
      row.availableVersion ||
      row.latest_available_version ||
      row.updateVersion ||
      row.update_version
  );
}

function compareVersionish(a: string, b: string): number | null {
  const left = a
    .split(/[^\dA-Za-z]+/)
    .filter(Boolean)
    .map((part) => (/^\d+$/.test(part) ? Number(part) : part.toLowerCase()));
  const right = b
    .split(/[^\dA-Za-z]+/)
    .filter(Boolean)
    .map((part) => (/^\d+$/.test(part) ? Number(part) : part.toLowerCase()));
  if (!left.length || !right.length) return null;
  const max = Math.max(left.length, right.length);
  for (let index = 0; index < max; index += 1) {
    const aPart = left[index] ?? 0;
    const bPart = right[index] ?? 0;
    if (aPart === bPart) continue;
    if (typeof aPart === "number" && typeof bPart === "number") return aPart - bPart;
    return String(aPart).localeCompare(String(bPart), undefined, { numeric: true });
  }
  return 0;
}

function softwareVersionStatus(row: Record<string, unknown>): {
  label: string;
  color: "success" | "warning" | "default";
  kind: AppLatestKind;
} {
  const current = softwareVersion(row);
  const latest = softwareLatestVersion(row);
  const status = String(row.latestVersionStatus || "")
    .trim()
    .toLowerCase();
  if (!latest && status === "not_applicable") {
    return { label: "System app", color: "default", kind: APP_LATEST_FILTER_SYSTEM };
  }
  if (!latest && status === "not_in_catalog") {
    return { label: "Not in catalog", color: "default", kind: APP_LATEST_FILTER_NOT_IN_CATALOG };
  }
  if (!latest && status === "pending") {
    return { label: "Lookup pending", color: "default", kind: APP_LATEST_FILTER_PENDING };
  }
  if (!current) return { label: "Version unknown", color: "default", kind: APP_LATEST_FILTER_UNKNOWN };
  if (!latest || latest === current) {
    return {
      label: latest ? "Current" : "Latest unavailable",
      color: latest ? "success" : "default",
      kind: latest ? APP_LATEST_FILTER_CURRENT : APP_LATEST_FILTER_UNKNOWN,
    };
  }
  const comparison = compareVersionish(current, latest);
  if (comparison !== null && comparison < 0) return { label: `Newer: ${latest}`, color: "warning", kind: APP_LATEST_FILTER_NEWER };
  return { label: "Current", color: "success", kind: APP_LATEST_FILTER_CURRENT };
}

function softwareLatestSortRank(kind: AppLatestKind): number {
  const index = APP_LATEST_FILTER_ORDER.indexOf(kind);
  return index >= 0 ? index : APP_LATEST_FILTER_ORDER.length;
}

/** Build Latest dropdown options from kinds actually present in the Applications rows. */
function buildAppLatestFilterOptions(softwareRows: Array<Record<string, unknown>>): Array<{ value: AppLatestFilter; label: string }> {
  const present = new Set<AppLatestKind>();
  for (const row of softwareRows) {
    present.add(softwareVersionStatus(row).kind);
  }
  const options: Array<{ value: AppLatestFilter; label: string }> = [
    { value: APP_LATEST_FILTER_ALL, label: APP_LATEST_FILTER_LABELS[APP_LATEST_FILTER_ALL] },
  ];
  for (const kind of APP_LATEST_FILTER_ORDER) {
    if (present.has(kind)) {
      options.push({ value: kind, label: APP_LATEST_FILTER_LABELS[kind] });
    }
  }
  return options;
}

function readableFieldLabel(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function keyValueRows(value: unknown): Array<{ key: string; label: string; value: unknown }> {
  const record = rawRecord(value);
  if (!record) return [];
  return Object.entries(record)
    .filter(([, item]) => item !== null && item !== undefined && item !== "" && !Array.isArray(item) && typeof item !== "object")
    .map(([key, item]) => ({ key, label: readableFieldLabel(key), value: item }));
}

function patchStatusLabel(metrics: RmmDeviceSnapshotSample["metrics"]): string {
  const patch = rawRecord(metrics?.patch_management);
  return rawString(patch?.patchStatus || patch?.status) || rawString(metrics?.software_status) || "—";
}

function isEmptyNinjaOnePatchSummary(key: string, value: unknown, provider: string): boolean {
  if (String(provider || "").toLowerCase() !== "ninjaone") return false;
  if (key === "patch_status" && /^no pending patch items$/i.test(rawString(value))) return true;
  const numberValue = numberOrNull(value);
  return numberValue === 0 && /(count|total|pending|failed|rejected|approved|installed|history)/i.test(key);
}

function getPatchSummaryRows(
  metrics: RmmDeviceSnapshotSample["metrics"],
  provider: string
): Array<{ key: string; label: string; value: unknown }> {
  const rows: Array<{ key: string; label: string; value: unknown }> = [];
  const seen = new Set<string>();
  const add = (key: string, label: string, value: unknown) => {
    if (value === null || value === undefined || value === "" || value === "—" || seen.has(key)) return;
    if (isEmptyNinjaOnePatchSummary(key, value, provider)) return;
    seen.add(key);
    rows.push({ key, label, value });
  };

  add("patch_status", "Patch Status", patchStatusLabel(metrics));
  add(
    "reboot_required",
    "Reboot Required",
    metrics?.reboot_required === null || metrics?.reboot_required === undefined ? null : metrics.reboot_required ? "Yes" : "No"
  );
  add("software_status", "Software Status", metrics?.software_status);
  for (const row of keyValueRows(metrics?.patch_management)) {
    add(`patch_${row.key}`, row.label, row.value);
  }
  return rows;
}

function rawCollectionRows(value: unknown): Array<Record<string, unknown>> {
  const direct = rawArray(value);
  if (direct.length) return direct;
  const record = rawRecord(value);
  if (!record) return [];
  const preferredKeys = [
    "patches",
    "installs",
    "softwarePatches",
    "softwarePatchInstalls",
    "osPatches",
    "osPatchInstalls",
    "jobs",
    "activeJobs",
    "data",
    "items",
    "results",
    "rows",
  ];
  for (const key of preferredKeys) {
    const rows = rawArray(record[key]);
    if (rows.length) return rows;
  }
  for (const item of Object.values(record)) {
    const rows = rawArray(item);
    if (rows.length) return rows;
  }
  return [];
}

interface PatchCollectionSection {
  key: string;
  title: string;
  rows: Array<Record<string, unknown>>;
}

function getPatchCollectionSections(sample: RmmDeviceSnapshotSample | null): PatchCollectionSection[] {
  const raw = rawRecord(sample?.raw);
  if (!raw) return [];
  return [
    { key: "os_patches", title: "OS Patches" },
    { key: "os_patch_installs", title: "OS Patch Installs" },
    { key: "software_patches", title: "Software Patches" },
    { key: "software_patch_installs", title: "Software Patch Installs" },
  ]
    .map((section) => ({ ...section, rows: rawCollectionRows(raw[section.key]) }))
    .filter((section) => section.rows.length > 0);
}

function getOperationalCollectionSections(sample: RmmDeviceSnapshotSample | null): PatchCollectionSection[] {
  const raw = rawRecord(sample?.raw);
  if (!raw) return [];
  return [
    { key: "active_jobs", title: "Active Jobs" },
    { key: "activities", title: "Recent Activities" },
    { key: "windows_services", title: "Windows Services" },
    { key: "network_interfaces", title: "Network Interfaces" },
    { key: "processors", title: "Processors" },
  ]
    .map((section) => ({ ...section, rows: rawCollectionRows(raw[section.key]) }))
    .filter((section) => section.rows.length > 0);
}

function collectionRowName(row: Record<string, unknown>, fallback: string): string {
  return (
    rawString(row.name) ||
    rawString(row.displayName) ||
    rawString(row.title) ||
    rawString(row.message) ||
    rawString(row.subject) ||
    rawString(row.interfaceName) ||
    rawString(row.adapterName) ||
    rawString(row.model) ||
    rawString(row.kbArticleId) ||
    rawString(row.patchId) ||
    rawString(row.id) ||
    rawString(row.uid) ||
    fallback
  );
}

function collectionRowStatus(row: Record<string, unknown>): string {
  return (
    rawString(row.status) ||
    rawString(row.jobStatus) ||
    rawString(row.patchStatus) ||
    rawString(row.approvalStatus) ||
    rawString(row.installStatus) ||
    rawString(row.state) ||
    rawString(row.statusCode) ||
    rawString(row.activityType) ||
    rawString(row.type) ||
    rawString(row.severity) ||
    rawString(row.priority) ||
    rawString(row.result) ||
    "—"
  );
}

function collectionRowDate(row: Record<string, unknown>): string {
  const value =
    row.activityTime ||
    row.logonTime ||
    row.installedAt ||
    row.installTime ||
    row.updateTime ||
    row.updatedAt ||
    row.createTime ||
    row.createdAt ||
    row.timestamp;
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return formatTime(numeric > 10_000_000_000 ? numeric : numeric * 1000);
  const parsed = Date.parse(String(value || ""));
  return Number.isFinite(parsed) ? formatTime(parsed) : "—";
}

function patchRowName(row: Record<string, unknown>): string {
  return collectionRowName(row, "Patch item");
}

function patchRowStatus(row: Record<string, unknown>): string {
  return collectionRowStatus(row);
}

function patchRowDate(row: Record<string, unknown>): string {
  return collectionRowDate(row);
}

const DeviceMetricTile: React.FC<{
  title: string;
  value: React.ReactNode;
  caption: React.ReactNode;
  icon: React.ReactNode;
  accent: string;
}> = ({ title, value, caption, icon, accent }) => (
  <Box sx={{ ...panelSx, p: 1.5, display: "flex", gap: 1.25, minWidth: 0 }}>
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: "6px",
        bgcolor: `${accent}14`,
        color: accent,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: "uppercase" }}>
        {title}
      </Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.25, overflowWrap: "anywhere" }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", overflowWrap: "anywhere" }}>
        {caption}
      </Typography>
    </Box>
  </Box>
);

type DeviceContextSectionKey = "overview" | "disk" | "operations" | "patch" | "alerts" | "applications" | "alertTickets";

const DEFAULT_CONTEXT_SECTION_COLLAPSED: Record<DeviceContextSectionKey, boolean> = {
  overview: false,
  disk: false,
  operations: false,
  patch: false,
  alerts: false,
  applications: false,
  alertTickets: false,
};

const DeviceContextSection: React.FC<{
  title: React.ReactNode;
  icon?: React.ReactNode;
  collapsed: boolean;
  onToggle: () => void;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, collapsed, onToggle, action, children }) => (
  <Box sx={{ ...panelSx, overflow: "hidden", flex: "0 0 auto" }}>
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: 58,
        userSelect: "none",
        "&:hover": { bgcolor: "#F8FAFC" },
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggle();
          }
        }}
        sx={{
          minWidth: 0,
          flex: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          borderRadius: "6px",
          outline: "none",
          "&:focus-visible": { outline: "2px solid #8B5CF6", outlineOffset: 2 },
        }}
      >
        {icon ? <Box sx={{ color: "text.secondary", display: "flex", alignItems: "center", flex: "0 0 auto" }}>{icon}</Box> : null}
        <Typography variant="subtitle2" sx={{ fontWeight: 800, minWidth: 0, overflowWrap: "anywhere" }}>
          {title}
        </Typography>
      </Box>
      {action ? <Box sx={{ flex: "0 0 auto" }}>{action}</Box> : null}
      <Tooltip title={collapsed ? "Expand section" : "Collapse section"}>
        <IconButton
          size="small"
          aria-label={collapsed ? "Expand section" : "Collapse section"}
          onClick={(event) => {
            event.stopPropagation();
            onToggle();
          }}
          sx={{ width: 30, height: 30, flex: "0 0 auto" }}
        >
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              transition: "transform 160ms ease",
              transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
            }}
          />
        </IconButton>
      </Tooltip>
    </Box>
    <Collapse in={!collapsed} timeout="auto" unmountOnExit>
      <Box sx={{ px: 1.5, pb: 1.5, minHeight: 0 }}>{children}</Box>
    </Collapse>
  </Box>
);

/**
 * Display title for a device conversation.
 * Placeholder `Device: {host}` is replaced by the graph once intent is clear.
 */
function deviceThreadTitle(thread: RmmDeviceThreadSummary): string {
  return rawString(thread.thread_name) || (thread.message_count ? `${thread.message_count} message${thread.message_count === 1 ? "" : "s"}` : "New conversation");
}

/** Past-conversations menu + New Chat for the device thread pane. */
const DeviceChatThreadControls: React.FC<{
  threads: RmmDeviceThreadSummary[];
  selectedThreadId: string | null;
  threadsLoading: boolean;
  threadActionBusy: boolean;
  onSelectThread: (threadId: string) => void;
  onNewThread: () => void;
  onDeleteThread: (threadId: string) => void;
}> = ({ threads, selectedThreadId, threadsLoading, threadActionBusy, onSelectThread, onNewThread, onDeleteThread }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(anchorEl);

  const handleSelectThread = (threadId: string) => {
    setAnchorEl(null);
    onSelectThread(threadId);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mr: 1 }}>
      <Tooltip title="Past device conversations">
        <span>
          <IconButton
            size="small"
            aria-label="Past device conversations"
            aria-controls={menuOpen ? "device-conversation-menu" : undefined}
            aria-haspopup="menu"
            aria-expanded={menuOpen ? "true" : undefined}
            onClick={(event) => setAnchorEl(event.currentTarget)}
            sx={{
              width: 32,
              height: 32,
              border: "1px solid #D7E3F5",
              bgcolor: "#F8FAFF",
              color: "#2563EB",
              "&:hover": { bgcolor: "#EFF6FF" },
            }}
          >
            <AddCommentIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        id="device-conversation-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 460,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: 420,
            border: "1px solid #E2E8F0",
            borderRadius: "6px",
            boxShadow: "0 18px 45px rgba(15, 23, 42, 0.16)",
          },
        }}
      >
        <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid #EEF2F7", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 800, color: "#111827" }}>
            Device Conversations
          </Typography>
          {threadsLoading ? <CircularProgress size={14} thickness={5} /> : <Chip size="small" label={threads.length} variant="outlined" />}
        </Box>
        {threadsLoading && !threads.length ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : threads.length === 0 ? (
          <MenuItem disabled sx={{ whiteSpace: "normal", py: 1.25 }}>
            <Typography variant="body2" color="text.secondary">
              No past conversations yet.
            </Typography>
          </MenuItem>
        ) : (
          threads.map((thread) => {
            const threadId = String(thread.ai_thread_id);
            const selected = selectedThreadId === threadId;
            return (
              <MenuItem
                key={threadId}
                selected={selected}
                onClick={() => handleSelectThread(threadId)}
                sx={{
                  alignItems: "flex-start",
                  gap: 0.75,
                  px: 1.25,
                  py: 1.1,
                  borderBottom: "1px solid #F1F5F9",
                  whiteSpace: "normal",
                  "&:last-of-type": { borderBottom: 0 },
                  "&.Mui-selected": { bgcolor: "#F3EEFF" },
                  "&.Mui-selected:hover": { bgcolor: "#EDE7FF" },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: selected ? 800 : 700, color: selected ? "#6D5BEF" : "#111827", overflowWrap: "anywhere" }}>
                    {deviceThreadTitle(thread)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: selected ? "#7C6CF0" : "#64748B", display: "block", mt: 0.25 }}>
                    {formatTime(thread.latest_message_at_ms || thread.updated_at_ms || thread.created_at_ms)}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: selected ? "#7C6CF0" : "#94A3B8", flex: "0 0 auto", mt: 0.35 }}>
                  #{thread.ai_thread_id}
                </Typography>
                <Tooltip title="Delete conversation">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={`Delete conversation #${thread.ai_thread_id}`}
                      disabled={threadActionBusy}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setAnchorEl(null);
                        onDeleteThread(threadId);
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        mt: 0.1,
                        flex: "0 0 auto",
                        color: "#DC2626",
                        "&:hover": { bgcolor: "rgba(220, 38, 38, 0.08)" },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </MenuItem>
            );
          })
        )}
      </Menu>
      <Button
        variant="contained"
        size="small"
        onClick={onNewThread}
        disabled={threadActionBusy}
        startIcon={threadActionBusy ? undefined : <AddIcon sx={{ fontSize: 18 }} />}
        sx={{
          borderRadius: "6px",
          textTransform: "none",
          minWidth: 104,
          minHeight: 32,
          px: 1.25,
          py: 0.45,
          boxShadow: "none",
          fontWeight: 700,
          fontFamily: "var(--theo-font-sans)",
          background: "#705D48",
          "&:hover": { background: "#5A4B3A", boxShadow: "none" },
        }}
      >
        {threadActionBusy ? <CircularProgress size={18} color="inherit" /> : "New Chat"}
      </Button>
    </Box>
  );
};

const DeviceContextPane: React.FC<{
  details: RmmDeviceSnapshotDetailsResponse;
  identity: DeviceIdentityParams | null;
  onBack: () => void;
  onTogglePane: () => void;
  onRefresh: () => void;
  refreshing?: boolean;
  refreshError?: string | null;
}> = ({ details, identity, onBack, onTogglePane, onRefresh, refreshing = false, refreshError = null }) => {
  const alertTicketsEnabled = isRmmAlertsLdEnabled(useFlags() as Record<string, unknown>);
  const latest = details.latest_sample;
  const metrics = displayDeviceMetrics(latest);
  const metadata = rawRecord(details.metadata) || {};
  const hostName = rawString(metadata.host_name) || latest?.host_name || details.device_id;
  const providerName = providerDisplayName(
    String(details.source?.provider || details.provider || metadata.provider || "")
  );
  const alerts = dedupeAlerts(getOpenAlertRows(latest));
  const softwareRows = getSoftwareRows(latest);
  const [applicationFilters, setApplicationFilters] = useState<ApplicationFilterState>({ name: "", latest: APP_LATEST_FILTER_ALL });
  const latestFilterOptions = buildAppLatestFilterOptions(softwareRows);
  const activeLatestFilter = latestFilterOptions.some((opt) => opt.value === applicationFilters.latest)
    ? applicationFilters.latest
    : APP_LATEST_FILTER_ALL;
  const filteredSoftwareRows = softwareRows
    .map((software, index) => ({ software, status: softwareVersionStatus(software), index }))
    .filter(({ software, status }) => {
      const nameNeedle = applicationFilters.name.trim().toLowerCase();
      if (nameNeedle && !softwareName(software).toLowerCase().includes(nameNeedle)) return false;
      if (activeLatestFilter !== APP_LATEST_FILTER_ALL && status.kind !== activeLatestFilter) return false;
      return true;
    })
    .sort((a, b) => softwareLatestSortRank(a.status.kind) - softwareLatestSortRank(b.status.kind) || a.index - b.index);
  const disks: RmmDeviceSnapshotDiskMetric[] = Array.isArray(metrics.disks) ? metrics.disks : [];
  const aggregateDisk = aggregateDiskUsage(metrics);
  const patchCollections = getPatchCollectionSections(latest);
  const resolvedProvider = String(
    details.source?.provider || details.provider || metadata.provider || ""
  ).toLowerCase();
  const patchSummary = getPatchSummaryRows(metrics, resolvedProvider);
  const capabilities =
    details.capabilities ||
    (metadata.capabilities as Record<string, boolean> | undefined) ||
    {};
  const showLiveStatus = capabilities.live_status === true;
  const showCpuMemory = capabilities.utilization_history === true;
  const showSoftware = capabilities.software !== false && capabilities.applications !== false;
  const showPatch = capabilities.patching === true;
  const showRmmOpenAlerts = capabilities.rmm_alerts === true || capabilities.theo_alert_history === true;
  const isIntune = resolvedProvider === "intune" || (capabilities.compliance === true && capabilities.utilization_history === false);
  const complianceState =
    rawString(asRecord(details.status).compliance_state) ||
    rawString(asRecord(metrics.compliance).state);
  const intuneStorage = asRecord(metrics.storage);
  const intuneMemoryBytes =
    numberOrNull(asRecord(metrics.memory).total_bytes) ||
    numberOrNull(metadata.physical_memory_bytes) ||
    numberOrNull(asRecord(metrics.hardware).physical_memory_bytes);
  const encryptionFlag = metadata.is_encrypted ?? asRecord(metrics.encryption).is_encrypted;
  const primaryUser = asRecord(metrics.primary_user);
  const hardwareMeta = asRecord(metrics.hardware);
  const operatingSystemLabel =
    rawString(metadata.operating_system) ||
    rawString(hardwareMeta.operating_system) ||
    rawString(metadata.platform) ||
    "";
  const osVersionLabel =
    rawString(metadata.os_version) || rawString(hardwareMeta.os_version) || "";
  const osFamily = resolveDeviceOsFamilyFromHints({
    osFamily: rawString(metadata.os_family),
    operatingSystem: operatingSystemLabel,
    platform: rawString(metadata.platform) || rawString(hardwareMeta.platform),
    osVersion: osVersionLabel,
  });
  const osFamilyLabel = deviceOsFamilyLabel(osFamily);
  const osTileValue = osFamilyLabel || operatingSystemLabel || "—";
  const osTileCaption =
    [operatingSystemLabel && operatingSystemLabel !== osTileValue ? operatingSystemLabel : null, osVersionLabel]
      .filter(Boolean)
      .join(" · ") || (osFamilyLabel ? "Reported OS" : "OS not reported");
  const operationalCollections = getOperationalCollectionSections(latest);
  const lastLoggedOnUser = rawRecord(latest?.raw?.last_logged_on_user);
  const operationSummary = [
    {
      key: "last_logged_on_user",
      label: "Last Logged On User",
      value: rawString(lastLoggedOnUser?.userName),
      caption: formatTime(lastLoggedOnUser?.logonTime),
    },
  ].filter((row) => row.value);
  const hasPatchSection = showPatch && (patchSummary.length > 0 || patchCollections.length > 0);
  const hasOperationsSection = operationSummary.length > 0 || operationalCollections.length > 0;
  const cpuUsed = percentOrNull(metrics.cpu?.used_percent);
  const cpuCores = numberOrNull(metrics.cpu?.total_cores);
  const cpuProcessorCount = numberOrNull(metrics.cpu?.processor_count);
  const [collapsedSections, setCollapsedSections] = useState<Record<DeviceContextSectionKey, boolean>>({
    ...DEFAULT_CONTEXT_SECTION_COLLAPSED,
  });

  useEffect(() => {
    setCollapsedSections({ ...DEFAULT_CONTEXT_SECTION_COLLAPSED });
    setApplicationFilters({ name: "", latest: APP_LATEST_FILTER_ALL });
  }, [details.source?.provider, details.provider, details.device_id]);

  const toggleSection = useCallback((section: DeviceContextSectionKey) => {
    setCollapsedSections((previous) => ({
      ...previous,
      [section]: !previous[section],
    }));
  }, []);

  return (
    <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column", ...panelSx, overflow: "hidden" }}>
      <DeviceDataNotices details={details} />
      <Box sx={{ p: 2, borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <Box sx={{ minWidth: 0, display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Tooltip title="Back to devices">
            <IconButton size="small" onClick={onBack} aria-label="Back to devices">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, overflowWrap: "anywhere" }}>
              {hostName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
              {rawString(metadata.customer_name) || "Unmapped customer"} · {providerName}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, flexShrink: 0 }}>
          <Tooltip title={refreshing ? "Refreshing device snapshot…" : "Refresh device snapshot"}>
            <span>
              <IconButton
                size="small"
                onClick={onRefresh}
                disabled={refreshing}
                aria-label="Refresh device snapshot"
                sx={{
                  backgroundColor: "#F1F5F9",
                  color: "#475569",
                  "&:hover": { backgroundColor: "#E2E8F0" },
                }}
              >
                {refreshing ? <CircularProgress size={18} /> : <RefreshIcon sx={{ fontSize: 20 }} />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Focus chat (hide device details)">
            <IconButton
              size="small"
              onClick={onTogglePane}
              aria-label="Focus chat, hide device details"
              sx={{
                backgroundColor: "#F1F5F9",
                color: "#475569",
                "&:hover": { backgroundColor: "#E2E8F0" },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {refreshError ? (
        <Alert severity="warning" sx={{ mx: 2, mt: 1.5, flexShrink: 0 }}>
          {refreshError}
        </Alert>
      ) : null}

      <Box
        sx={{
          p: 2,
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          scrollbarGutter: "stable",
        }}
      >
        <DeviceContextSection
          title="Overview"
          icon={<ComputerIcon fontSize="small" />}
          collapsed={collapsedSections.overview}
          onToggle={() => toggleSection("overview")}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            {showLiveStatus ? (
              <DeviceMetricTile
                title="Status"
                value={metrics.online === false ? "Offline" : metrics.online === true ? "Online" : "Unknown"}
                caption={`Last seen ${formatTime(metrics.last_seen_ms)}`}
                icon={<ComputerIcon fontSize="small" />}
                accent={metrics.online === false ? "#64748B" : "#059669"}
              />
            ) : (
              <DeviceMetricTile
                title="Last check-in"
                value={formatTime(metrics.last_seen_ms || metadata.last_seen_ms)}
                caption={providerName}
                icon={<ComputerIcon fontSize="small" />}
                accent="#059669"
              />
            )}
            {isIntune ? (
              <DeviceMetricTile
                title="Compliance"
                value={complianceState || "Unknown"}
                caption={rawString(metadata.device_category) || "Intune"}
                icon={<SecurityIcon fontSize="small" />}
                accent={String(complianceState).toLowerCase() === "compliant" ? "#059669" : "#D97706"}
              />
            ) : null}
            {showRmmOpenAlerts ? (
              <DeviceMetricTile
                title="Alerts"
                value={sectionHasData(latest, "alerts") ? `${alerts.length} current` : "Unavailable"}
                caption={`Snapshot ${formatTime(latest?.sampled_at_ms)}`}
                icon={<WarningAmberIcon fontSize="small" />}
                accent={alerts.length ? "#DC2626" : "#059669"}
              />
            ) : null}
            {showCpuMemory ? (
              <DeviceMetricTile
                title="CPU"
                value={cpuUsed === null ? (cpuCores ? `${cpuCores} cores` : "—") : `${cpuUsed.toFixed(1)}% used`}
                caption={
                  cpuUsed === null
                    ? cpuProcessorCount
                      ? `${cpuProcessorCount} processor${cpuProcessorCount === 1 ? "" : "s"}`
                      : (resolvedProvider === "datto" ? "Installed memory from Datto audit" : "Usage not reported")
                    : rawString(metrics.cpu?.usage_source) || "Reported by RMM"
                }
                icon={<ComputerIcon fontSize="small" />}
                accent="#2563EB"
              />
            ) : null}
            {showCpuMemory ? (
              <DeviceMetricTile
                title="Memory"
                value={formatBytes(metrics.memory?.total_bytes)}
                caption={metrics.memory?.used_percent == null ? "Usage not reported" : `${metrics.memory.used_percent}% used`}
                icon={<MemoryIcon fontSize="small" />}
                accent="#0F766E"
              />
            ) : isIntune ? (
              <DeviceMetricTile
                title="Memory"
                value={intuneMemoryBytes ? formatBytes(intuneMemoryBytes) : "—"}
                caption={rawString(metadata.model) || "Intune hardware"}
                icon={<MemoryIcon fontSize="small" />}
                accent="#0F766E"
              />
            ) : null}
            <DeviceMetricTile
              title="Disk"
              value={
                aggregateDisk
                  ? `${aggregateDisk.used_percent.toFixed(1)}% used`
                  : percentOrNull(intuneStorage.used_percent) != null
                    ? `${Number(percentOrNull(intuneStorage.used_percent)).toFixed(1)}% used`
                    : "—"
              }
              caption={
                aggregateDisk
                  ? `${formatBytes(aggregateDisk.used_bytes)} used of ${formatBytes(aggregateDisk.total_bytes)}`
                  : numberOrNull(intuneStorage.total_bytes) != null
                    ? `${formatBytes(intuneStorage.used_bytes)} used of ${formatBytes(intuneStorage.total_bytes)}`
                    : "Usage not reported"
              }
              icon={<StorageIcon fontSize="small" />}
              accent={
                (aggregateDisk && aggregateDisk.used_percent >= 90) ||
                Number(percentOrNull(intuneStorage.used_percent) || 0) >= 90
                  ? "#DC2626"
                  : "#D97706"
              }
            />
            {isIntune ? (
              <DeviceMetricTile
                title="Encryption"
                value={encryptionFlag === true ? "Encrypted" : encryptionFlag === false ? "Not encrypted" : "—"}
                caption={rawString(metadata.ownership) || "Ownership unknown"}
                icon={<SecurityIcon fontSize="small" />}
                accent="#2563EB"
              />
            ) : null}
            {showSoftware ? (
              <DeviceMetricTile
                title="Apps"
                value={sectionCount(latest, "software", softwareRows.length)}
                caption={isIntune ? "Intune detected apps" : "Audited software"}
                icon={<AppsIcon fontSize="small" />}
                accent="#7C3AED"
              />
            ) : null}
            {isIntune ? (
              <DeviceMetricTile
                title="Primary user"
                value={
                  rawString(metadata.primary_user_display_name) ||
                  rawString(metadata.primary_user_upn) ||
                  rawString(primaryUser.display_name) ||
                  rawString(primaryUser.upn) ||
                  "—"
                }
                caption={rawString(metadata.primary_user_upn) || undefined}
                icon={<ComputerIcon fontSize="small" />}
                accent="#334155"
              />
            ) : null}
            <DeviceMetricTile
              title="OS"
              value={osTileValue}
              caption={osTileCaption}
              icon={
                <DeviceOsIcon
                  osFamily={osFamily}
                  operatingSystem={operatingSystemLabel || osFamilyLabel}
                  size={18}
                />
              }
              accent="#475569"
            />
          </Box>
        </DeviceContextSection>

        <DeviceContextSection
          title="Disk"
          icon={<StorageIcon fontSize="small" sx={{ color: "#D97706" }} />}
          collapsed={collapsedSections.disk}
          onToggle={() => toggleSection("disk")}
        >
          {disks.length ? (
            <Box>
              {disks.map((disk, index) => {
                const usedPercent = percentOrNull(disk.used_percent);
                return (
                  <Box key={`${diskLabel(disk)}-${index}`} sx={{ py: 1, borderTop: index === 0 ? "none" : "1px solid #E2E8F0" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
                        {diskLabel(disk)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                        {usedPercent === null ? "—" : `${usedPercent.toFixed(1)}% used`}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={usedPercent === null ? 0 : Math.max(0, Math.min(100, usedPercent))}
                      color={usedPercent !== null && usedPercent >= 90 ? "error" : usedPercent !== null && usedPercent >= 75 ? "warning" : "primary"}
                      sx={{ height: 8, borderRadius: 8, bgcolor: "#E2E8F0" }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                      {formatBytes(disk.free_bytes)} free of {formatBytes(disk.size_bytes)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No disk audit data.
            </Typography>
          )}
        </DeviceContextSection>

        {hasOperationsSection ? (
          <DeviceContextSection
            title={`${providerName} Inventory`}
            icon={<ComputerIcon fontSize="small" sx={{ color: "#2563EB" }} />}
            collapsed={collapsedSections.operations}
            onToggle={() => toggleSection("operations")}
          >
            {operationSummary.length ? (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: operationalCollections.length ? 1.5 : 0 }}>
                {operationSummary.map((row) => (
                  <Box key={row.key} sx={{ p: 1, borderRadius: "6px", bgcolor: "#F8FAFC", minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      {row.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, overflowWrap: "anywhere" }}>
                      {row.value}
                    </Typography>
                    {row.caption !== "—" ? (
                      <Typography variant="caption" color="text.secondary">
                        {row.caption}
                      </Typography>
                    ) : null}
                  </Box>
                ))}
              </Box>
            ) : null}
            {operationalCollections.map((section) => (
              <Box key={section.key} sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>
                  {section.title}
                </Typography>
                <TableContainer sx={{ maxHeight: 260 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, bgcolor: "#F8FAFC" }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: "#F8FAFC" }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: "#F8FAFC" }}>Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {section.rows.map((item, index) => (
                        <TableRow key={`${section.key}-${collectionRowName(item, "Inventory item")}-${index}`}>
                          <TableCell sx={{ overflowWrap: "anywhere" }}>{collectionRowName(item, "Inventory item")}</TableCell>
                          <TableCell sx={{ overflowWrap: "anywhere" }}>{collectionRowStatus(item)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{collectionRowDate(item)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
          </DeviceContextSection>
        ) : null}

        {hasPatchSection ? (
          <DeviceContextSection
            title="Patch"
            icon={<SecurityIcon fontSize="small" sx={{ color: metrics.reboot_required ? "#D97706" : "#2563EB" }} />}
            collapsed={collapsedSections.patch}
            onToggle={() => toggleSection("patch")}
          >
            {patchSummary.length ? (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: patchCollections.length ? 1.5 : 0 }}>
                {patchSummary.map((row) => (
                  <Box key={row.key} sx={{ p: 1, borderRadius: "6px", bgcolor: "#F8FAFC", minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      {row.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, overflowWrap: "anywhere" }}>
                      {formatUnknownValue(row.value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : null}
            {patchCollections.map((section) => (
              <Box key={section.key} sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>
                  {section.title}
                </Typography>
                <TableContainer sx={{ maxHeight: 260 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, bgcolor: "#F8FAFC" }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: "#F8FAFC" }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, bgcolor: "#F8FAFC" }}>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {section.rows.map((patch, index) => (
                        <TableRow key={`${section.key}-${patchRowName(patch)}-${index}`}>
                          <TableCell sx={{ overflowWrap: "anywhere" }}>{patchRowName(patch)}</TableCell>
                          <TableCell sx={{ overflowWrap: "anywhere" }}>{patchRowStatus(patch)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{patchRowDate(patch)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
          </DeviceContextSection>
        ) : null}

        {showRmmOpenAlerts ? (
          <DeviceContextSection
            title={`Current ${providerName} Alerts`}
            icon={<WarningAmberIcon fontSize="small" sx={{ color: alerts.length ? "#DC2626" : "#059669" }} />}
            collapsed={collapsedSections.alerts}
            onToggle={() => toggleSection("alerts")}
          >
            {alerts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {sectionHasData(latest, "alerts") ? `No open ${providerName} alerts.` : "Alert data is unavailable."}
              </Typography>
            ) : (
              <Box sx={{ maxHeight: 260, overflowY: "auto", pr: 0.5 }}>
                {alerts.map((alert, index) => (
                  <Box key={`${alertDescription(alert)}-${index}`} sx={{ py: 1, borderTop: index === 0 ? "none" : "1px solid #E2E8F0" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25, flexWrap: "wrap" }}>
                      <Chip
                        size="small"
                        label={rawString(alert.priority || alert.severity || rawField(alert, "alertContext", "type")) || "Alert"}
                        variant="outlined"
                        color="warning"
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatTime(alertTimestamp(alert))}
                      </Typography>
                      {alert.duplicate_count > 1 ? <Chip size="small" label={`${alert.duplicate_count} matching`} variant="outlined" /> : null}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
                      {alertDescription(alert)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </DeviceContextSection>
        ) : null}

        {alertTicketsEnabled && ["datto", "ninjaone"].includes(resolvedProvider) && identity ? (
          <DeviceContextSection
            title="Alert tickets"
            icon={<WarningAmberIcon fontSize="small" sx={{ color: "#7C3AED" }} />}
            collapsed={collapsedSections.alertTickets}
            onToggle={() => toggleSection("alertTickets")}
          >
            <DeviceAlertHistory identity={identity} deviceName={hostName} />
          </DeviceContextSection>
        ) : null}

        {showSoftware ? (
        <DeviceContextSection
          title="Applications"
          icon={<AppsIcon fontSize="small" sx={{ color: "#7C3AED" }} />}
          collapsed={collapsedSections.applications}
          onToggle={() => toggleSection("applications")}
          action={
            <Chip
              size="small"
              label={sectionHasData(latest, "software") ? (filteredSoftwareRows.length === softwareRows.length ? `${softwareRows.length}` : `${filteredSoftwareRows.length} of ${softwareRows.length}`) : sectionCount(latest, "software", 0)}
              variant="outlined"
            />
          }
        >
          <TableContainer sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#F8FAFC", width: "44%", minWidth: 170 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: "#64748B" }}>
                        Name
                      </Typography>
                      <TextField
                        value={applicationFilters.name}
                        onChange={(event) => setApplicationFilters((previous) => ({ ...previous, name: event.target.value }))}
                        size="small"
                        placeholder="Filter name..."
                        sx={{ width: "100%", ...compactFilterSx }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#F8FAFC", width: "24%", minWidth: 100 }}>Current</TableCell>
                  <TableCell sx={{ fontWeight: 700, bgcolor: "#F8FAFC", width: "32%", minWidth: 140 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: "#64748B" }}>
                        Latest
                      </Typography>
                      <TextField
                        select
                        value={activeLatestFilter}
                        onChange={(event) =>
                          setApplicationFilters((previous) => ({ ...previous, latest: event.target.value as AppLatestFilter }))
                        }
                        size="small"
                        sx={{ width: "100%", ...compactFilterSx }}
                      >
                        {latestFilterOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSoftwareRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        {sectionHasData(latest, "software") ? (softwareRows.length ? "No applications match the current filters." : "No installed applications were reported.") : "Application inventory is unavailable."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSoftwareRows.map(({ software, status, index }) => (
                    <TableRow key={`${softwareName(software)}-${softwareVersion(software)}-${index}`}>
                      <TableCell sx={{ overflowWrap: "anywhere" }}>{softwareName(software) || "—"}</TableCell>
                      <TableCell sx={{ overflowWrap: "anywhere" }}>{softwareVersion(software) || "—"}</TableCell>
                      <TableCell>
                        <Chip size="small" label={status.label} color={status.color} variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DeviceContextSection>
        ) : null}
      </Box>
    </Box>
  );
};

const RmmDeviceDetail: React.FC = () => {
  const routeParams = useParams<{ sourceType: string; sourceId: string; deviceId: string; threadId?: string }>();
  const sourceType = String(routeParams.sourceType || "").trim();
  const sourceId = String(routeParams.sourceId || "").trim();
  const deviceId = String(routeParams.deviceId || "").trim();
  const routeThreadId = String(routeParams.threadId || "").trim();
  // Avoid a new object each render — thread load effects depend on this reference.
  const identity = useMemo<DeviceIdentityParams | null>(() => {
    if (!sourceType || !sourceId || !deviceId) return null;
    return { sourceType, sourceId, deviceId };
  }, [sourceType, sourceId, deviceId]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state: any) => state.auth.user);
  const { details, loading, error: detailsError, refreshing: contextRefreshing, refreshError: contextRefreshError, refresh: refreshDeviceContext } = useDeviceDetails(identity);
  const [pageError, setError] = useState<string | null>(null);
  const error = pageError || detailsError;
  const [aiThreadId, setAiThreadId] = useState<string | null>(null);
  const [deviceThreads, setDeviceThreads] = useState<RmmDeviceThreadSummary[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState<string | null>(null);
  const loadedThreadRef = useRef<{ device: string; thread: string } | null>(null);
  const [threadActionBusy, setThreadActionBusy] = useState(false);
  const [threadIdPendingDelete, setThreadIdPendingDelete] = useState<string | null>(null);
  const [contextPaneCollapsed, setContextPaneCollapsed] = useState(false);
  const deviceSessionKey = identity ? `${identity.sourceType}/${identity.sourceId}/${identity.deviceId}` : null;

  const hostName = useMemo(() => {
    const metadata = rawRecord(details?.metadata) || {};
    return rawString(metadata.host_name) || details?.latest_sample?.host_name || deviceId;
  }, [details, deviceId]);

  const buildDeviceThreadPath = useCallback(
    (threadId: string) =>
      `/devices/${encodeURIComponent(sourceType)}/${encodeURIComponent(sourceId)}/${encodeURIComponent(deviceId)}/${encodeURIComponent(threadId)}`,
    [sourceType, sourceId, deviceId]
  );

  const refreshDeviceThreads = useCallback(
    async (threadIdentity: DeviceIdentityParams) => {
      setThreadsLoading(true);
      try {
        const response = await DevicesApi.listDeviceThreads(threadIdentity);
        setDeviceThreads(response.threads || []);
      } finally {
        setThreadsLoading(false);
      }
    },
    []
  );

  /** Sync graph-generated thread title into the local device-thread list (not the Chat sidebar). */
  const updateActiveDeviceThreadName = useCallback(
    (threadName: string) => {
      const normalizedName = String(threadName || "").trim();
      if (!aiThreadId || !normalizedName) return;
      setDeviceThreads((threads) =>
        threads.map((thread) =>
          String(thread.ai_thread_id) === String(aiThreadId)
            ? { ...thread, thread_name: normalizedName }
            : thread
        )
      );
    },
    [aiThreadId]
  );

  const applyDeviceThreadResponse = useCallback(
    (response: any) => {
      const thread = response?.ai_thread;
      const createdThreadId = thread?.ai_thread_id || response?.ai_thread_id;
      if (!createdThreadId) throw new Error("Device thread response did not include ai_thread_id");
      const threadId = String(createdThreadId);
      loadedThreadRef.current = { device: `${sourceType}/${sourceId}/${deviceId}`, thread: threadId };
      setAiThreadId(threadId);
      setThreadLoading(false);
      dispatch({
        type: ActionTypes.GET_USER_THREAD,
        payload: {
          ai_thread: {
            ...thread,
            ai_thread_id: createdThreadId,
          },
        },
      });
      dispatch(ChatAction.setSelectedUserThreadId(threadId));
      if (Array.isArray(response?.threads)) {
        setDeviceThreads(response.threads);
      }
      return threadId;
    },
    [dispatch, sourceType, sourceId, deviceId]
  );

  useEffect(() => {
    if (!deviceSessionKey || !currentUser?.email || !identity) return;
    if (loadedThreadRef.current?.device === deviceSessionKey && loadedThreadRef.current.thread === routeThreadId) {
      setThreadLoading(false);
      return;
    }
    let cancelled = false;
    setThreadLoading(true);
    setThreadError(null);
    setAiThreadId(null);
    dispatch({ type: ActionTypes.CLEAR_CHAT });
    const loadThread = routeThreadId
      ? DevicesApi.getDeviceThread(identity, routeThreadId)
      : DevicesApi.getOrCreateDeviceThread(identity);
    loadThread
      .then((response) => {
        if (!cancelled) {
          const loadedThreadId = applyDeviceThreadResponse(response);
          if (!routeThreadId || routeThreadId !== loadedThreadId) {
            navigate(buildDeviceThreadPath(loadedThreadId), { replace: !routeThreadId });
          }
        }
      })
      .catch((err) => {
        if (!cancelled) setThreadError(err instanceof Error ? err.message : "Failed to load device chat");
      })
      .finally(() => {
        if (!cancelled) setThreadLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    applyDeviceThreadResponse,
    buildDeviceThreadPath,
    currentUser?.email,
    deviceSessionKey,
    identity,
    dispatch,
    navigate,
    routeThreadId,
  ]);

  const openDeviceThread = useCallback(
    (threadId: string) => {
      if (!threadId || threadId === aiThreadId) return;
      navigate(buildDeviceThreadPath(threadId));
    },
    [aiThreadId, buildDeviceThreadPath, navigate]
  );

  const createNewDeviceThread = useCallback(async () => {
    if (!details) return;
    setThreadActionBusy(true);
    setError(null);
    try {
      dispatch({ type: ActionTypes.CLEAR_CHAT });
      const threadIdentity = requireDeviceIdentity(identity, details);
      const response = await DevicesApi.createDeviceThread(threadIdentity);
      const threadId = applyDeviceThreadResponse(response);
      navigate(buildDeviceThreadPath(threadId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create a new device chat");
    } finally {
      setThreadActionBusy(false);
    }
  }, [applyDeviceThreadResponse, buildDeviceThreadPath, details, identity, dispatch, navigate, refreshDeviceThreads]);

  /** Delete a device thread; if it was selected, open the next remaining thread or create one. */
  const deleteDeviceThreadById = useCallback(
    async (threadIdToDelete: string) => {
      if (!details || !threadIdToDelete) return;
      setThreadActionBusy(true);
      setError(null);
      setThreadIdPendingDelete(null);
      const deletingCurrent = String(aiThreadId || "") === String(threadIdToDelete);
      try {
        const threadIdentity = requireDeviceIdentity(identity, details);
        await DevicesApi.deleteDeviceThread(threadIdentity, threadIdToDelete);
        const numericThreadId = Number(threadIdToDelete);
        dispatch({
          type: ActionTypes.DELETE_USER_THREAD,
          payload: Number.isFinite(numericThreadId) ? numericThreadId : threadIdToDelete,
        });

        if (!deletingCurrent) {
          await refreshDeviceThreads(threadIdentity);
          setDeviceThreads(threads => threads.filter(thread => String(thread.ai_thread_id) !== String(threadIdToDelete)));
          return;
        }

        dispatch({ type: ActionTypes.CLEAR_CHAT });
        setAiThreadId(null);
        const threadsResponse = await DevicesApi.listDeviceThreads(threadIdentity);
        const remainingThreads = (threadsResponse.threads || []).filter((thread) => String(thread.ai_thread_id) !== String(threadIdToDelete));
        setDeviceThreads(remainingThreads);
        const nextThreadId = remainingThreads[0]?.ai_thread_id ? String(remainingThreads[0].ai_thread_id) : "";
        if (nextThreadId) {
          navigate(buildDeviceThreadPath(nextThreadId), { replace: true });
        } else {
          const response = await DevicesApi.createDeviceThread(threadIdentity);
          const threadId = applyDeviceThreadResponse(response);
          navigate(buildDeviceThreadPath(threadId), { replace: true });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to delete device chat");
      } finally {
        setThreadActionBusy(false);
      }
    },
    [aiThreadId, applyDeviceThreadResponse, buildDeviceThreadPath, details, identity, dispatch, navigate, refreshDeviceThreads]
  );

  const requestDeleteDeviceThread = useCallback((threadId: string) => {
    if (!threadId) return;
    setThreadIdPendingDelete(String(threadId));
  }, []);

  useEffect(() => {
    document.title = `${hostName || "Device"} — Theo`;
    return () => {
      document.title = "Theo";
    };
  }, [hostName]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, height: "100%", p: 1.5, bgcolor: "#F5F7FB" }}>
      <PanelGroup direction="horizontal">
        <Panel defaultSize={contextPaneCollapsed ? 96 : 58} minSize={contextPaneCollapsed ? 70 : 42}>
          <Box sx={{ height: "100%", minHeight: 0, display: "flex" }}>
            {aiThreadId && !threadLoading ? (
              <NeoAssistant
                key={aiThreadId}
                ticketId={null}
                ticketDisplayId={null}
                userId={currentUser?.email || ""}
                currentUser={currentUser}
                customerId={rawString(rawRecord(details?.metadata)?.customer_id) || null}
                aiThreadId={aiThreadId}
                skipAutoInitialize
                fillAvailableHeight
                ticketTitle={hostName}
                ticketLink={window.location.href}
                showAiOverview={false}
                nullStatePrompts={["Review this device", "Summarize current alerts", "Check risky applications"]}
                onThreadName={updateActiveDeviceThreadName}
                headerRightAction={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mr: 1 }}>
                    <DeviceChatThreadControls
                      threads={deviceThreads}
                      selectedThreadId={aiThreadId}
                      threadsLoading={threadsLoading}
                      threadActionBusy={threadActionBusy}
                      onSelectThread={openDeviceThread}
                      onNewThread={createNewDeviceThread}
                      onDeleteThread={requestDeleteDeviceThread}
                    />
                    <Tooltip title="Delete this device conversation">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (aiThreadId) requestDeleteDeviceThread(aiThreadId);
                          }}
                          disabled={threadActionBusy || !aiThreadId}
                          sx={{
                            width: 32,
                            height: 32,
                            border: "1px solid #FECACA",
                            bgcolor: "#FEF2F2",
                            color: "#DC2626",
                            "&:hover": { bgcolor: "#FEE2E2" },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                }
              />
            ) : (
              <Box sx={{ ...panelSx, flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {threadError ? <Alert severity="warning">{threadError}</Alert> : <CircularProgress size={28} />}
              </Box>
            )}
          </Box>
        </Panel>

        {contextPaneCollapsed ? (
          <Panel defaultSize={4} minSize={4} maxSize={6}>
            <Box sx={{ height: "100%", minHeight: 0, pl: 1, display: "flex" }}>
              <Box
                sx={{
                  ...panelSx,
                  borderRadius: "8px",
                  flex: 1,
                  minWidth: 46,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#FFFFFF",
                }}
              >
                <Tooltip title="Show device details">
                  <IconButton
                    size="small"
                    aria-label="Show device details"
                    onClick={() => setContextPaneCollapsed(false)}
                    sx={{
                      backgroundColor: "#F1F5F9",
                      color: "#475569",
                      "&:hover": { backgroundColor: "#E2E8F0" },
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Panel>
        ) : (
          <>
            <PanelResizeHandle style={{ width: 12, background: "transparent" }}>
              <Divider orientation="vertical" flexItem sx={{ height: "100%", mx: "5px", borderColor: "transparent" }} />
            </PanelResizeHandle>

            <Panel defaultSize={42} minSize={28} maxSize={50}>
              {details ? <DeviceContextPane
                details={details}
                identity={identity}
                onBack={() => navigate("/msp-analytics?panel=devices")}
                onTogglePane={() => setContextPaneCollapsed(true)}
                onRefresh={refreshDeviceContext}
                refreshing={contextRefreshing}
                refreshError={contextRefreshError || detailsError || error}
              /> : <Box sx={{ p: 3 }}>{loading ? <CircularProgress /> : <Alert severity="warning">{error || "Device not found"}</Alert>}</Box>}
            </Panel>
          </>
        )}
      </PanelGroup>
      <DeleteModal
        open={Boolean(threadIdPendingDelete)}
        onCancel={() => setThreadIdPendingDelete(null)}
        onDelete={async () => {
          if (threadIdPendingDelete) {
            await deleteDeviceThreadById(threadIdPendingDelete);
          }
        }}
        title="Delete Device Conversation?"
        description={
          threadIdPendingDelete && String(threadIdPendingDelete) !== String(aiThreadId || "")
            ? `This deletes device conversation #${threadIdPendingDelete}. The current chat stays open.`
            : "This deletes the current Theo conversation for this device."
        }
        confirmText="Delete"
      />
    </Box>
  );
};

export default RmmDeviceDetail;
